import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AntApp, useNotification } from '@thisisdmg/tirecloud-patterns';
import { SolidButton as Button, Modal } from '@thisisdmg/tirecloud-patterns/v2';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import qs from 'query-string';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import {
  createBrowserRouter,
  NavigationType,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';
import { match } from 'ts-pattern';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import GenericErrorElement from './features/errors/routes/index';
import './i18n';
// import AppUpdateModal from './pwa-update-modal';
import routes from './routes';
// import useSwVersion from './useSwVersion';

import PWAManager from '@/features/app/components/pwa-manager';
import appConfig from '@/features/app/constants/app-config';
import captureSentryException from '@/features/app/functions/captureSentryException';
import getSavedCookieConsentSetting from '@/features/app/functions/getSavedCookieConsentSetting';
import staleQueryIfNotError from '@/features/app/functions/staleQueryIfNotError';
import { useAppDispatch } from '@/features/app/hooks/useAppDispatch';
import useAppSelector from '@/features/app/hooks/useAppSelector';
import useAdminSettingsQuery from '@/features/app/queries/useAdminSettingsQuery';
import appStore from '@/features/app/stores/app-store';
import reduxHistoryStateSlice from '@/features/app/stores/redux-history-state-slice';
import reduxMiscStateSlice from '@/features/app/stores/redux-misc-state-slice';
import isAccessTokenStillValid from '@/features/auth/functions/isAccessTokenStillValid';
import isRefreshTokenStillValid from '@/features/auth/functions/isRefreshTokenStillValid';
import legacy_parseAndValidateAccessToken from '@/features/auth/functions/legacy_parseAndValidateAccessToken';
import useCanClientUseCookieTokenStorage from '@/features/auth/hooks/useCanClientUseCookieTokenStorage';
import useGetUserWithAccessToken from '@/features/auth/hooks/useGetUserWithAccessToken';
import useHasUserLoggedInBefore from '@/features/auth/hooks/useHasUserLoggedInBefore';
import useLogoutUser from '@/features/auth/hooks/useLogoutUser';
import useProcessLoginResponse from '@/features/auth/hooks/useProcessLoginResponse';
import useRefreshAccessToken from '@/features/auth/hooks/useRefreshAccessToken';
import useValidateAndGetUserFromStorage from '@/features/auth/hooks/useValidateAndGetUserFromStorage';
import reduxAuthStateSlice from '@/features/auth/stores/redux-auth-state-slice';
import GenericErrorComponent from '@/features/errors/components/generic-error-component';
import CustomTrans from '@/shared/components/custom-trans';
import { ShepherdJourneyProvider } from 'react-shepherd';

function UserLoader(props: PropsWithChildren) {
  const {
    data: adminSettings,
    isFetching: isAdminSettingFetching,
    isError: isAdminSettingError,
  } = useAdminSettingsQuery({
    refetchOnMount: true,
  });
  const featureFlags = useFeatureFlags();

  const authState = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const refreshToken = useRefreshAccessToken();
  const canClientUseCookieStorage = useCanClientUseCookieTokenStorage();
  const notification = useNotification();
  const processLoginResponse = useProcessLoginResponse();

  const hasUserLoggedInBefore = useHasUserLoggedInBefore();
  const [checkedAccessTokenValidity, setCheckedAccessTokenValidity] = useState(false);
  const [checkedRefreshTokenValidity, setCheckedRefreshTokenValidity] = useState(false);
  const getUserWithAccessToken = useGetUserWithAccessToken();

  const isRefreshingToken = useRef(false);
  const attemptToRefreshToken = useCallback(
    (initializeAuthState = false, preloadedLegacyToken?: string) => {
      if (isRefreshingToken.current) {
        return;
      }

      // wait for admin settings to be fetched
      if (isAdminSettingFetching) return;
      // if fetching admin setting is failed, we cannot proceed, better display error page
      if (isAdminSettingError) return;

      if (!checkedAccessTokenValidity) return;
      if (!(featureFlags.auth.enableRefreshToken && checkedRefreshTokenValidity)) return;

      const autoLogout = hasUserLoggedInBefore();

      if (!featureFlags.auth.enableRefreshToken) {
        dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout }));
        return;
      }

      if (!authState.isRefreshTokenStillValid) {
        dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout }));
        return;
      }

      let rememberMe = false;
      let storedRefreshToken;

      if (featureFlags.auth.useCookieTokenStorage) {
        rememberMe = Cookies.get(appConfig.rememberMeKey) === 'true';
      } else if (featureFlags.auth.useLegacyTokenStorage) {
        const auth_data = Cookies.get(appConfig.authTokenKey);
        if (auth_data) {
          const maybeToken = legacy_parseAndValidateAccessToken(auth_data, false);
          if (maybeToken) {
            rememberMe = maybeToken.rememberMe ?? false;
            storedRefreshToken = maybeToken.refreshToken;
          }
        }
      }

      // debugger
      isRefreshingToken.current = true;
      return refreshToken
        .mutateAsync(preloadedLegacyToken ?? storedRefreshToken)
        .then((data) => {
          const token = processLoginResponse({
            ...data,
            rememberMe,
          });

          if (initializeAuthState) {
            let promise;

            if (featureFlags.auth.useCookieTokenStorage) {
              promise = getUserWithAccessToken({
                useCookieStorage: true,
              });
            } else if (featureFlags.auth.useLegacyTokenStorage && token?.value) {
              promise = getUserWithAccessToken({
                useCookieStorage: false,
                token: token.value,
              });
            } else {
              // no authentication mechanism is enabled
              throw new Error('page.auth.login.form.feedback.generalLoginError');
            }

            promise
              .then((user) => {
                dispatch(reduxAuthStateSlice.actions.loginUser({ token, user }));
                // if cookie storage enabled, remove the old token cookie to migrate to cookie storage
                if (
                  !featureFlags.auth.useLegacyTokenStorage ||
                  featureFlags.auth.useCookieTokenStorage
                ) {
                  Cookies.remove(appConfig.authTokenKey);
                }
              })
              .catch((e) => {
                captureSentryException(e, {
                  message: 'failed to load user data',
                  tags: {
                    feature: 'auth.user-loader',
                  },
                });
                notification.error({
                  message: <CustomTrans i18nKey={'component.general.apiCallsFeedback.userData'} />,
                  duration: 0,
                });
                dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout }));
              });
          } else {
            // update the token only if the legacy token storage is used
            if (
              featureFlags.auth.useLegacyTokenStorage &&
              !featureFlags.auth.useCookieTokenStorage
            ) {
              dispatch(reduxAuthStateSlice.actions.updateToken(token));
            } else {
              // remove the old token cookie since we're migrating to cookie storage
              Cookies.remove(appConfig.authTokenKey);
              dispatch(
                reduxAuthStateSlice.actions.updateTokenValidity({
                  isAccessTokenStillValid: true,
                  isRefreshTokenStillValid: true,
                })
              );
            }
          }
        })
        .catch((e) => {
          notification.error({
            message: <CustomTrans i18nKey={'component.general.apiCallsFeedback.refreshFailed'} />,
            duration: 0,
          });
          captureSentryException(e, {
            message: 'failed to refresh token',
            tags: {
              feature: 'auth.token-refresher',
            },
          });
          dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout }));
        })
        .finally(() => {
          isRefreshingToken.current = false;
        });
    },
    [
      isAdminSettingFetching,
      isAdminSettingError,
      checkedAccessTokenValidity,
      featureFlags.auth.enableRefreshToken,
      featureFlags.auth.useCookieTokenStorage,
      featureFlags.auth.useLegacyTokenStorage,
      checkedRefreshTokenValidity,
      hasUserLoggedInBefore,
      authState.isRefreshTokenStillValid,
      refreshToken,
      dispatch,
      processLoginResponse,
      getUserWithAccessToken,
      notification,
    ]
  );

  const loadingUserInProgress = useRef(false);
  const validateAndGetUserFromStorage = useValidateAndGetUserFromStorage();

  // skip the first render
  useEffect(() => {
    if (loadingUserInProgress.current || isRefreshingToken.current) {
      return;
    }

    if (authState.initialized) return;
    // skip if authState is properly initialized
    if (authState.initialized) return;
    // wait for admin settings to be fetched
    if (isAdminSettingFetching) return;
    // if fetching admin setting is failed, we cannot proceed, better display error page
    if (isAdminSettingError) return;

    if (!checkedAccessTokenValidity) return;
    if (featureFlags.auth.enableRefreshToken && !checkedRefreshTokenValidity) return;

    // if the access token is not valid, attempt to refresh it
    if (!authState.isAccessTokenStillValid && featureFlags.auth.enableRefreshToken) {
      attemptToRefreshToken(true);
      return;
    }

    loadingUserInProgress.current = true;
    // time to load user either using Cookie storage or legacy storage
    validateAndGetUserFromStorage(
      featureFlags.auth.useCookieTokenStorage,
      featureFlags.auth.useLegacyTokenStorage
    )
      .then((authData) => {
        if (authData.user) {
          // if cookie storage enabled, but client is not using it, attempt to refresh the token
          // to set new cookie
          if (
            featureFlags.auth.useCookieTokenStorage &&
            !canClientUseCookieStorage &&
            featureFlags.auth.enableRefreshToken
          ) {
            attemptToRefreshToken(true, authData.token?.value);
          } else {
            dispatch(reduxAuthStateSlice.actions.loginUser(authData));
          }
        } else if (featureFlags.auth.enableRefreshToken) {
          attemptToRefreshToken(true, authData.token?.refreshToken);
        } else {
          dispatch(reduxAuthStateSlice.actions.initializeAuthState());
        }
      })
      .catch((e) => {
        if (e instanceof AxiosError && e.response?.status === 401) {
          // don't show notification if it's 401
          if (featureFlags.auth.enableRefreshToken) {
            // debugger
            attemptToRefreshToken(true);
          } else {
            notification.error({
              message: <CustomTrans i18nKey={'component.general.apiCallsFeedback.userData'} />,
              duration: 0,
            });
            dispatch(reduxAuthStateSlice.actions.initializeAuthState());
          }
        } else {
          notification.error({
            message: <CustomTrans i18nKey={'component.general.apiCallsFeedback.userData'} />,
            duration: 0,
          });
          captureSentryException(e, {
            message: 'failed to load user data',
            tags: {
              feature: 'auth.user-loader',
            },
          });
          dispatch(reduxAuthStateSlice.actions.initializeAuthState());
        }
      })
      .finally(() => {
        loadingUserInProgress.current = false;
      });
  }, [
    authState.isAccessTokenStillValid,
    featureFlags.auth.enableRefreshToken,
    featureFlags.auth.useCookieTokenStorage,
    featureFlags.auth.useLegacyTokenStorage,
    attemptToRefreshToken,
    authState,
    canClientUseCookieStorage,
    dispatch,
    isAdminSettingError,
    isAdminSettingFetching,
    notification,
    checkedAccessTokenValidity,
    checkedRefreshTokenValidity,
    validateAndGetUserFromStorage,
  ]);

  // periodically check if the access token is still valid
  useEffect(() => {
    if (isAdminSettingFetching) return;
    if (isAdminSettingError) return;
    if (!adminSettings) return;

    const updateAccessTokenValidity = () => {
      const _isAccessTokenStillValid = isAccessTokenStillValid(
        featureFlags.auth.useCookieTokenStorage,
        featureFlags.auth.useLegacyTokenStorage
      );
      dispatch(reduxAuthStateSlice.actions.updateAccessTokenValidity(_isAccessTokenStillValid));
    };

    updateAccessTokenValidity();
    setCheckedAccessTokenValidity(true);
    const interval = setInterval(updateAccessTokenValidity, appConfig.tokenCheckInterval);
    return () => clearInterval(interval);
  }, [adminSettings, dispatch, isAdminSettingError, isAdminSettingFetching]);

  // periodically check if the refresh token is still valid
  useEffect(() => {
    if (isAdminSettingFetching) return;
    if (isAdminSettingError) return;
    if (!adminSettings) return;
    if (!featureFlags.auth.enableRefreshToken) return;

    const updateRefreshTokenValidity = () => {
      const _isRefreshTokenStillValid = isRefreshTokenStillValid(
        featureFlags.auth.useCookieTokenStorage,
        featureFlags.auth.useLegacyTokenStorage
      );
      dispatch(reduxAuthStateSlice.actions.updateRefreshTokenValidity(_isRefreshTokenStillValid));
    };

    updateRefreshTokenValidity();
    setCheckedRefreshTokenValidity(true);
    const interval = setInterval(updateRefreshTokenValidity, appConfig.tokenCheckInterval);
    return () => clearInterval(interval);
  }, [adminSettings, dispatch, isAdminSettingError, isAdminSettingFetching]);

  useEffect(() => {
    if (!authState.initialized) return;
    if (isAdminSettingFetching) return;
    if (isAdminSettingError) return;

    // if the user is already logged in, and the access token is no longer valid, attempt to refresh it
    if (authState.user && !authState.isAccessTokenStillValid) {
      if (featureFlags.auth.enableRefreshToken) {
        attemptToRefreshToken(false);
      } else {
        dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout: true }));
      }
    }
  }, [
    authState.isAccessTokenStillValid,
    attemptToRefreshToken,
    authState,
    isAdminSettingError,
    isAdminSettingFetching,
    featureFlags.auth.enableRefreshToken,
    dispatch,
  ]);

  useEffect(() => {
    const handleApiCallReturns401 = () => {
      if (featureFlags.auth.enableRefreshToken) {
        attemptToRefreshToken(authState.user === null);
      } else {
        dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout: true }));
      }
    };
    window.addEventListener('app:apiCallReturns401', handleApiCallReturns401);
    return () => {
      window.removeEventListener('app:apiCallReturns401', handleApiCallReturns401);
    };
  }, [featureFlags.auth.enableRefreshToken, attemptToRefreshToken, authState.user, dispatch]);

  if (isAdminSettingError) {
    return (
      <GenericErrorComponent
        errorCode={[5, 0, 0]}
        errorMessage={t('page.errors.globalCatchAllError.title')}
        errorDetails={
          <CustomTrans
            i18nKey={'page.errors.globalCatchAllError.description'}
            components={{
              ErrorDetailsElem: <></>,
            }}
          />
        }
        actionButton={
          <Button
            color="brand"
            onClick={async () => {
              try {
                // clear all caches
                if (window.caches !== undefined) {
                  const keys = await window.caches.keys();
                  await Promise.all(keys.map((key) => window.caches.delete(key)));
                }
              } catch (e) {
                captureSentryException(e, {
                  message: 'failed to clear caches',
                  tags: {
                    feature: 'core.pwa',
                  },
                });
              }
              window.location.reload();
            }}
          >
            {t('page.errors.globalCatchAllError.reloadBtn')}
          </Button>
        }
      />
    );
  }

  return props.children;
}

function InnerBrowserRouter() {
  const location = useLocation();
  const { user, initialized } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const { data: adminSettings } = useAdminSettingsQuery();
  const notification = useNotification();
  const isIFrame = window !== window.parent;
  const navigationType = useNavigationType();
  const dispatch = useAppDispatch();

  const titleCacheRef = useRef(appConfig.defaultDocumentTitle);
  const lastTrackedPath = useRef('');
  const consentSettings = getSavedCookieConsentSetting();
  const navigate = useNavigate();
  const history = useAppSelector((state) => state.routerHistory.history);
  const authState = useAppSelector((state) => state.auth);
  const logoutUser = useLogoutUser();
  const [, setSearchParams] = useSearchParams();

  const goBack = useCallback(() => {
    if (
      history.length === 1 &&
      // for webview, there's only two entry point (either products page or login page)
      // the rest of pages always go back to those two pages
      (location.pathname === `/products` || location.pathname === `/auth/login`)
      //  &&
      // window.webviewToNativeBridge?.closeApp !== undefined
    ) {
      // window.webviewToNativeBridge.closeApp();
      return;
    }
    navigate(-1);
  }, [history, location, navigate]);

  useEffect(() => {
    // listen app:go-back event
    window.addEventListener('app:go-back', goBack);
    return () => {
      window.removeEventListener('app:go-back', goBack);
    };
  }, [goBack]);

  const trackPageView = useCallback(() => {
    if (initialized !== true) return;
    if (window._paq === undefined) return;

    const title = document.title;
    if (title === appConfig.defaultDocumentTitle) return;

    if (consentSettings['matomo'] === true) window._paq.push(['setConsentGiven']);
    if (user) window._paq.push(['setUserId', user.userNumber]);
    window._paq.push(['deleteCustomVariables', 'page']);
    window._paq.push(['setCustomUrl', location.pathname]);
    window._paq.push(['setDocumentTitle', title]);
    window._paq.push(['trackPageView']);

    lastTrackedPath.current = location.pathname;
  }, [initialized, consentSettings, user, location.pathname]);

  useEffect(() => {
    if (navigationType === NavigationType.Push) {
      dispatch(reduxHistoryStateSlice.actions.addHistory(location));
    } else if (navigationType === NavigationType.Pop) {
      dispatch(reduxHistoryStateSlice.actions.popHistory());
    } else if (navigationType === NavigationType.Replace) {
      dispatch(reduxHistoryStateSlice.actions.replaceHistory(location));
    }
  }, [dispatch, location, navigationType]);

  const firstRender = useRef(true);
  const haveTrackedCurrentPageURL = useRef(false);
  useEffect(() => {
    if (import.meta.env.DEV && firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!haveTrackedCurrentPageURL.current) {
      haveTrackedCurrentPageURL.current = true;
      dispatch(reduxHistoryStateSlice.actions.addHistory(location));
    }
  }, [dispatch, location]);

  const isMaintenanceNotificationShown = useRef(false);

  useEffect(() => {
    if (
      !isMaintenanceNotificationShown.current &&
      adminSettings &&
      adminSettings.upcomingMaintenance &&
      adminSettings.upcomingMaintenance.start < Date.now() / 1000 &&
      adminSettings.upcomingMaintenance.end > Date.now() / 1000
    ) {
      isMaintenanceNotificationShown.current = true;
      if (!isIFrame)
        notification.open({
          message: adminSettings.upcomingMaintenance!.title,
          description: adminSettings.upcomingMaintenance!.message,
          duration: 0,
          type: 'info',
        });
    }
  }, [adminSettings, isMaintenanceNotificationShown, notification, isIFrame]);
  useEffect(() => {
    const processNetworkStatus = () => {
      if (navigator.onLine) {
        dispatch(reduxMiscStateSlice.actions.setOnline());
      } else {
        dispatch(reduxMiscStateSlice.actions.setOffline());
        notification.warning({
          message: <CustomTrans i18nKey={'component.general.offlineDetection.title'} />,
          description: t('component.general.offlineDetection.description'),
          duration: 10,
          showProgress: true,
          pauseOnHover: true,
        });
      }
    };
    window.addEventListener('offline', processNetworkStatus);
    window.addEventListener('online', processNetworkStatus);
    window.addEventListener('load', processNetworkStatus);

    return () => {
      window.removeEventListener('offline', processNetworkStatus);
      window.removeEventListener('online', processNetworkStatus);
      window.removeEventListener('load', processNetworkStatus);
    };
  }, [dispatch, notification, t]);

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={{
        objectToSearchString: qs.stringify,
        searchStringToObject: qs.parse,
      }}
    >
      <ShepherdJourneyProvider>
        <Helmet
          defaultTitle={appConfig.defaultDocumentTitle}
          titleTemplate={`%s - ${appConfig.defaultDocumentTitle}`}
          defer={false}
          onChangeClientState={(newState) => {
            if (
              newState.title !== titleCacheRef.current &&
              lastTrackedPath.current !== location.pathname &&
              !location.pathname.startsWith('/mockup')
            ) {
              titleCacheRef.current = newState.title;
              trackPageView();
            }
          }}
        />
        <Modal
          open={authState.autoLogout}
          footer={null}
          onCancel={() => {
            logoutUser(() => {
              setSearchParams({ action: 'logout' });
            }).then(() => {
              dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout: false }));
            });
          }}
          closeButtonProps={false}
        >
          <h2 className="tw:text-(length:--font-size-heading-5)">
            {t('page.auth.sessionExpiredModal.title')}
          </h2>
          <p className="tw:mb-(--spacing-7)">{t('page.auth.sessionExpiredModal.description')}</p>
          <div className="tw:flex tw:justify-end tw:mt-(--spacing-4)">
            <Button
              color="brand"
              onClick={() => {
                if (window._paq) {
                  window._paq.push([
                    'trackEvent',
                    'User',
                    'Click',
                    '"Session Expired" Modal Login',
                  ]);
                }
                logoutUser(() => {
                  setSearchParams({ action: 'logout' });
                }).then(() => {
                  dispatch(reduxAuthStateSlice.actions.logoutUser({ autoLogout: false }));
                });
              }}
            >
              {t('page.auth.sessionExpiredModal.loginBtn')}
            </Button>
          </div>
        </Modal>
        <Outlet />
        <ScrollRestoration />
      </ShepherdJourneyProvider>
    </QueryParamProvider>
  );
}

const Router = Sentry.wrapCreateMemoryRouterV6(createBrowserRouter)(
  [
    {
      element: <InnerBrowserRouter />,
      errorElement: <GenericErrorElement />,
      children: routes,
    },
  ],
  {
    basename: import.meta.env.REACT_APP_PREFIX,
  }
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'always',
      refetchOnWindowFocus: false,
      ...staleQueryIfNotError(appConfig.defaultDataStaleTime),
    },
  },
});

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const loader = document.querySelector('.loader-in-shell');
    if (loader) loader.remove();
  }, []);

  useEffect(() => {
    dayjs.updateLocale(i18n.language, {
      weekStart: match(i18n.language)
        .with('he', () => 0)
        .otherwise(() => 1),
    });
  }, [i18n.language]);

  return (
    <Sentry.ErrorBoundary>
      <Helmet>
        <html
          lang={i18n.language}
          dir={i18n.dir(i18n.language)}
          className="tc-theme-default light"
          data-theme="light"
          data-has-v2-components="true"
        />
      </Helmet>
      <PWAManager currentVersion={import.meta.env.REACT_APP_CHROMIUM_LIKE_VERSION} enabled>
        <Provider store={appStore}>
          <AntApp theme="default" direction={i18n.dir(i18n.language)}>
            <QueryClientProvider client={queryClient}>
              <UserLoader>
                <RouterProvider router={Router} />
              </UserLoader>
            </QueryClientProvider>
          </AntApp>
        </Provider>
      </PWAManager>
    </Sentry.ErrorBoundary>
  );
}

export default App;
